import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import { Pivot } from '../../libs/react-pivot';
import { useSetAtom } from 'jotai';
import { useIntl } from 'react-intl';
import { addMetadataObject, getCustomizedToolbar } from './utils/constants';
import { openSnackbarAdviseAtom } from '../../Atoms';

const PivotTable = forwardRef(function PivotTable(
  {
    visible,
    data,
    metadata,
    defaultConfiguration,
    handleChangeCurrentConfiguration,
  },
  ref
) {
  const intl = useIntl();
  const pivotRef = useRef(null);
  const prevVisible = useRef(visible);
  const openSnackbar = useSetAtom(openSnackbarAdviseAtom);
  const dataWithMetadata = useMemo(
    () => addMetadataObject(data, metadata),
    [data, metadata]
  );
  const [pivotKey, setPivotKey] = useState(0);

  const [currentReport, setCurrentReport] = useState({
    ...defaultConfiguration,
    dataSource: {
      data: dataWithMetadata,
    },
  });

  const onReportChange = () => {
    if (pivotRef.current) {
      const newReport = pivotRef.current.auraPivot.getReport();
      setCurrentReport(newReport);
      if (handleChangeCurrentConfiguration)
        handleChangeCurrentConfiguration(newReport);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      getReport: () => pivotRef.current?.auraPivot?.getReport?.() || null,
    }),
    []
  );

  const applyFullConfiguration = () => {
    if (!pivotRef.current || !defaultConfiguration) return;
    pivotRef.current.auraPivot.setReport({
      slice: defaultConfiguration.slice,
      options: defaultConfiguration.options,
      formats: defaultConfiguration.formats,
      calculatedFields: defaultConfiguration.calculatedFields || [],
      dataSource: {
        dataSourceType: 'json',
        data: dataWithMetadata,
      },
    });
  };

  useEffect(() => {
    if (defaultConfiguration) {
      setCurrentReport({
        ...defaultConfiguration,
        dataSource: {
          data: dataWithMetadata,
        },
      });
    }
  }, [defaultConfiguration, dataWithMetadata]);

  useEffect(() => {
    if (!visible) return;
    applyFullConfiguration();
  }, [defaultConfiguration, dataWithMetadata, visible, pivotKey]);

  useEffect(() => {
    if (visible && !prevVisible.current) {
      setPivotKey((key) => key + 1);
    }
    prevVisible.current = visible;
  }, [visible]);

  const handleSaveConfiguration = () => {
    if (pivotRef.current?.auraPivot) {
      const report = pivotRef.current.auraPivot.getReport();
      // eslint-disable-next-line no-unused-vars
      const { dataSource, ...reportWithoutData } = report;
      openSnackbar({
        severity: 'success',
        message: intl.messages.CONFIGURATION_SAVED,
      });
    }
  };

  const pivotGlobal = useMemo(
    () => ({
      options: {
        grid: {
          type: 'compact',
          showHeaders: false,
        },
        drillThrough: false,
        sorting: 'columns',
        configuratorButton: false,
      },
      dataSource: {
        dataSourceType: 'json',
        data: dataWithMetadata,
      },
    }),
    [dataWithMetadata]
  );

  const handleResetConfiguration = () => {
    setCurrentReport({
      ...defaultConfiguration,
      dataSource: {
        data: dataWithMetadata,
      },
    });
    openSnackbar({
      severity: 'success',
      message: intl.messages.CONFIGURATION_RESTORED,
    });
  };

  return (
    <Pivot
      key={pivotKey}
      ref={(ref) => {
        pivotRef.current = ref;
      }}
      width="100%"
      height="100%"
      toolbar
      locale={intl.locale}
      reportchange={onReportChange}
      beforeToolbarCreated={(toolbar) =>
        getCustomizedToolbar(
          toolbar?.toolbar || toolbar,
          handleSaveConfiguration,
          handleResetConfiguration,
          intl
        )
      }
      global={pivotGlobal}
    />
  );
});

PivotTable.propTypes = {
  data: PropTypes.object,
  visible: PropTypes.bool,
  metadata: PropTypes.object,
  defaultConfiguration: PropTypes.object,
  localStorageKey: PropTypes.string,
};

export default PivotTable;
